import { differenceInCalendarDays, differenceInDays } from 'date-fns'

import type { Parcel } from '../api/types'
import { de } from '../i18n/de'
import { parseDate } from './format'

/**
 * Amazon ships most orders itself, and its mail carries no carrier tracking number — such
 * a parcel is keyed on the order number instead, with `tracking_number` just holding that
 * order number normalised (dashes/spaces/dots stripped, uppercased). When that holds, the
 * UI shows and copies the order number rather than the normalised digit blob.
 */
export function isOrderKeyed(parcel: Parcel): parcel is Parcel & { order_number: string } {
  return (
    parcel.carrier_code === 'AMAZON' &&
    !!parcel.order_number &&
    parcel.tracking_number === parcel.order_number.replace(/[\s\-.]/g, '').toUpperCase()
  )
}

/**
 * After how many silent days a mail-only parcel is worth pointing at.
 *
 * Long enough that a slow but normal delivery does not trip it, short enough to notice
 * before the parcel is forgotten.
 */
export const STALE_AFTER_DAYS = 14

/**
 * How long a parcel has been silent, when that is worth showing — otherwise null.
 *
 * Only for carriers without an API (`mailOnly`): those parcels move only when a mail
 * arrives, and for some shipments none ever does, so the parcel would sit on "Unterwegs"
 * forever. A carrier with an API is being polled, so its silence means nothing happened.
 */
export function staleDays(parcel: Parcel, mailOnly: boolean): number | null {
  if (!mailOnly || parcel.status === 'DELIVERED' || parcel.archived_at) return null
  const last = parseDate(parcel.last_event_at)
  if (!last) return null
  const days = differenceInDays(new Date(), last)
  return days > STALE_AFTER_DAYS ? days : null
}

/**
 * What a row is called. The description when there is one; otherwise who sent it, so a
 * row never has an empty title. `fallback` lets the row set the stand-in apart.
 */
export function parcelTitle(parcel: Parcel): { text: string; fallback: boolean } {
  if (parcel.description) return { text: parcel.description, fallback: false }
  return {
    text: de.parcel.fallbackTitle(parcel.sender_name ?? parcel.carrier_name),
    fallback: true,
  }
}

/**
 * The company's own sites by postal code. A parcel going to one of them is tagged with the
 * site's name, which says more at a glance than the street address.
 */
const SITES: Record<string, string> = {
  '85375': 'Neufahrn',
  '85748': 'Garching',
}

/**
 * A short "where to" for the row: the site's name for our own sites, otherwise the town.
 * Null when there is no address at all.
 */
export function siteLabel(parcel: Parcel): string | null {
  const address = parcel.recipient_address
  if (!address) return null
  const match = address.match(/\b(\d{5})\s+([^,\n]+)/)
  if (match) {
    const site = SITES[match[1]]
    if (site) return site
    return titleCase(match[2].trim())
  }
  // A bare town from the carrier ("Aschheim") or a free-form line: show its last part.
  const parts = address.split(/[,\n]/).map((part) => part.trim()).filter(Boolean)
  return parts.length ? titleCase(parts[parts.length - 1]) : null
}

/** Carriers shout town names ("NEUFAHRN B. FREISING"); say them like a person would. */
function titleCase(value: string): string {
  if (value !== value.toUpperCase()) return value
  return value.toLowerCase().replace(/(^|[\s\-(])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase())
}

export type DayGroup = 'attention' | 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none'

/**
 * Order of the day groups inside "Unterwegs": what needs someone first, then today, then
 * what is late, then the days ahead; undated last.
 */
export const DAY_GROUP_ORDER: DayGroup[] = [
  'attention',
  'today',
  'overdue',
  'tomorrow',
  'week',
  'later',
  'none',
]

/**
 * Which day a parcel on its way belongs to.
 *
 * A parcel ready for pickup or with a problem waits for a person, not for a date, so it
 * leads. On the van means today whatever the announced window said. Otherwise the end of
 * the expected window decides; one that has passed without a delivery is overdue.
 */
export function dayGroup(parcel: Parcel, now = new Date()): DayGroup {
  if (parcel.status === 'PICKUP_READY' || parcel.status === 'EXCEPTION') return 'attention'
  if (parcel.status === 'OUT_FOR_DELIVERY') return 'today'
  const expected = parseDate(parcel.expected_delivery_to ?? parcel.expected_delivery_from)
  if (!expected) return 'none'
  const days = differenceInCalendarDays(expected, now)
  if (days < 0) return 'overdue'
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days < 7) return 'week'
  return 'later'
}

/** Days a parcel is past the end of its expected window, or null when it is not. */
export function overdueDays(parcel: Parcel, now = new Date()): number | null {
  if (parcel.status === 'DELIVERED' || parcel.archived_at) return null
  const expected = parseDate(parcel.expected_delivery_to ?? parcel.expected_delivery_from)
  if (!expected) return null
  const days = differenceInCalendarDays(now, expected)
  return days > 0 ? days : null
}

/** Wants someone today: on the van, waiting at a pickup point, or stuck. */
export function needsAttentionToday(parcel: Parcel): boolean {
  return (
    parcel.status === 'OUT_FOR_DELIVERY' ||
    parcel.status === 'PICKUP_READY' ||
    parcel.status === 'EXCEPTION'
  )
}
